import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoProgressBar } from "@redotech/arbiter/progress-bar/redo-progress-bar";
import { Text } from "@redotech/arbiter/text";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { openOrderDashboardPath } from "@redotech/redo-merchant-app-common/open-order-notifications";
import { memo, useEffect, useState } from "react";
import type { BehaviorSubject } from "rxjs";

interface ProgressNotificationProps {
  dismiss: () => void;
  percentage: number;
  showDashboardLink?: boolean;
  subtext?: string;
  /** Second line under the subtext, for a hint about what the bar means. */
  note?: string;
  teamId: string;
  title: string;
}

/** Follows a progress subject and ticks an ellipsis while it reports running. */
export function useProgressSubject<T extends { status: string }>(
  progress: BehaviorSubject<T>,
): { value: T; ellipsis: string } {
  const [value, setValue] = useState(progress.value);
  const [ellipsis, setEllipsis] = useState(".");

  useEffect(() => {
    const subscription = progress.subscribe(setValue);
    return () => subscription.unsubscribe();
  }, [progress]);

  useEffect(() => {
    if (value.status !== "running") {
      return;
    }
    const interval = setInterval(() => {
      setEllipsis((current) => (current.length >= 3 ? "." : current + "."));
    }, 1000);
    return () => clearInterval(interval);
  }, [value.status]);

  return { value, ellipsis };
}

export const ProgressNotification = memo(function ProgressNotification({
  dismiss,
  note,
  percentage,
  showDashboardLink = true,
  subtext,
  teamId,
  title,
}: ProgressNotificationProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Flex dir="column" gap="none" w="xxs">
      <Flex align="center" justify="space-between" px="xl" py="md">
        <Text fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
        <Flex align="center" gap="sm">
          <RedoButton
            IconTrailing={expanded ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => setExpanded((current) => !current)}
            size="xs"
          />
          <RedoButton IconTrailing={XIcon} onClick={dismiss} size="xs" />
        </Flex>
      </Flex>
      {expanded && (
        <Flex dir="column" gap="sm" pb="md" px="xl" w="full">
          <RedoProgressBar
            labelStyle="tooltip"
            percentage={percentage}
            style="thin"
          />
          {subtext && (
            <Text fontSize="xs" textColor="secondary">
              {subtext}
            </Text>
          )}
          {note && (
            <Text fontSize="xs" textColor="tertiary">
              {note}
            </Text>
          )}
          {showDashboardLink && (
            <RedoButton
              hierarchy="link"
              onClick={() => {
                const navigate = window.__redoOpenOrderNavigate;
                if (!navigate) {
                  return;
                }
                navigate(openOrderDashboardPath(teamId));
              }}
              size="xs"
              text="View on dashboard"
            />
          )}
        </Flex>
      )}
    </Flex>
  );
});
